// Contrail V3.9.0 — Saved places: named pins for a company (depot, client site, fuel stop, mooring…).
// Saved from the location card (right-click or Find a location), managed in the "Saved places" tool under Zones.
// Visible to everyone in the company; added by any member except viewers; edited/deleted by whoever added it,
// the company owner, or Skyward staff with live access. Show/hide is remembered in this browser.
import { useEffect, useMemo, useState } from "react";
import type { GeoJSONSource, Map as MLMap, MapLayerMouseEvent } from "maplibre-gl";
import { supabase } from "../../lib/supabase";
import { orgApi } from "../../lib/org";
import { onStyleReady } from "../../lib/mapready";
import { dms } from "../../lib/geo";
import ColourPicker, { rememberColour } from "../../ui/ColourPicker";
import { ToolPanel } from "./MapTools";

export type Place = { id: string; org_id: string; name: string; lat: number; lng: number; color: string; notes: string | null;
  created_by: string | null; created_at: string; updated_at: string };
export type PlaceDraft = { id?: string; org_id?: string; name: string; lat: number; lng: number; color: string; notes: string };
export type PlaceOrg = { id: string; name: string };

const SHOW_KEY = "fw.places.show", HIDE_KEY = "fw.places.hidden";
const readSet = () => { try { return new Set<string>(JSON.parse(localStorage.getItem(HIDE_KEY) ?? "[]")); } catch { return new Set<string>(); } };

export function usePlaces() {
  const [places, setPlaces] = useState<Place[]>([]);
  const [show, setShowState] = useState<boolean>(() => { try { return localStorage.getItem(SHOW_KEY) !== "0"; } catch { return true; } });
  const [hidden, setHiddenState] = useState<Set<string>>(readSet);
  const reload = async () => {
    const { data, error } = await supabase.from("saved_places").select("*").order("name");
    if (!error) setPlaces((data ?? []) as Place[]);   // keep what we have if the reload fails
  };
  useEffect(() => { reload(); }, []);
  const setShow = (v: boolean) => { setShowState(v); try { localStorage.setItem(SHOW_KEY, v ? "1" : "0"); } catch { /* ignore */ } };
  const toggleOne = (id: string) => {
    const n = new Set(hidden); n.has(id) ? n.delete(id) : n.add(id);
    setHiddenState(n); try { localStorage.setItem(HIDE_KEY, JSON.stringify([...n])); } catch { /* ignore */ }
  };
  return { places, reload, show, setShow, hidden, toggleOne };
}

/** Pins + names on the map. Re-added whenever the style reloads; clicking a pin calls onPick. */
export function usePlacesLayer(map: MLMap | null, places: Place[], show: boolean, hidden: Set<string>, onPick: (p: Place) => void) {
  useEffect(() => {
    if (!map) return;
    const data = { type: "FeatureCollection" as const, features: places.filter((p) => !hidden.has(p.id)).map((p) => ({
      type: "Feature" as const, properties: { id: p.id, name: p.name, c: p.color }, geometry: { type: "Point" as const, coordinates: [p.lng, p.lat] } })) };
    const draw = () => {
      const src = map.getSource("tf-places") as GeoJSONSource | undefined;
      if (src) src.setData(data);
      else {
        map.addSource("tf-places", { type: "geojson", data });
        map.addLayer({ id: "tf-places-pin", type: "circle", source: "tf-places",
          paint: { "circle-radius": 7, "circle-color": ["get", "c"], "circle-stroke-color": "#fff", "circle-stroke-width": 2.5 } });
        map.addLayer({ id: "tf-places-label", type: "symbol", source: "tf-places",
          layout: { "text-field": ["get", "name"], "text-size": 11.5, "text-offset": [0, 1.2], "text-anchor": "top", "text-optional": true },
          paint: { "text-color": "#111827", "text-halo-color": "#fff", "text-halo-width": 1.4 } });
      }
      for (const id of ["tf-places-pin", "tf-places-label"]) map.setLayoutProperty(id, "visibility", show ? "visible" : "none");
    };
    onStyleReady(map, draw, "places");
    // self-heal: if a style change ever drops our layers, put them back
    const heal = () => { if (!map.getLayer("tf-places-pin")) onStyleReady(map, draw, "places"); };
    map.on("styledata", heal);
    return () => { map.off("styledata", heal); };
  }, [map, places, show, hidden]);

  useEffect(() => {
    if (!map) return;
    const click = (e: MapLayerMouseEvent) => {
      const id = e.features?.[0]?.properties?.id;
      const p = places.find((x) => x.id === id);
      if (p) { e.preventDefault(); (e.originalEvent as any).__fwPlace = true; onPick(p); }
    };
    const enter = () => { map.getCanvas().style.cursor = "pointer"; };
    const leave = () => { map.getCanvas().style.cursor = ""; };
    map.on("click", "tf-places-pin", click);
    map.on("mouseenter", "tf-places-pin", enter);
    map.on("mouseleave", "tf-places-pin", leave);
    return () => { map.off("click", "tf-places-pin", click); map.off("mouseenter", "tf-places-pin", enter); map.off("mouseleave", "tf-places-pin", leave); };
  }, [map, places, onPick]);
}

export function PlacesTool({ map, places, show, setShow, hidden, toggleOne, orgs, canEdit, onEdit, onClose, reload }: {
  map: MLMap; places: Place[]; show: boolean; setShow: (v: boolean) => void; hidden: Set<string>; toggleOne: (id: string) => void;
  orgs: PlaceOrg[]; canEdit: (p: Place) => boolean; onEdit: (d: PlaceDraft) => void; onClose: () => void; reload: () => Promise<void>;
}) {
  const [q, setQ] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const list = useMemo(() => places.filter((p) => !q.trim() || p.name.toLowerCase().includes(q.trim().toLowerCase())), [places, q]);
  const orgName = (id: string) => orgs.find((o) => o.id === id)?.name ?? "";
  const multi = new Set(places.map((p) => p.org_id)).size > 1;
  async function remove(p: Place) {
    if (!confirm(`Delete the saved place “${p.name}”?`)) return;
    setErr(null);
    try { await orgApi("DELETE", `places/${p.id}`); await reload(); } catch (e: any) { setErr(e.message); }
  }
  return (
    <ToolPanel title="Saved places" onClose={onClose}>
      <label className="zt-show"><input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} /> Show saved places on the map</label>
      <p className="sub">To add a place, right-click the map (or use Find a location) and choose “Save place”.</p>
      {places.length > 6 && <input className="zt-search" placeholder="Search places" value={q} onChange={(e) => setQ(e.target.value)} />}
      {err && <div className="error">{err}</div>}
      <div className="zt-list">
        <ul>
          {list.map((p) => (
            <li key={p.id} className={hidden.has(p.id) ? "pl-off" : ""}>
              <button type="button" className="pl-eye" onClick={() => toggleOne(p.id)} aria-pressed={!hidden.has(p.id)}
                title={hidden.has(p.id) ? "Hidden on the map — click to show" : "Shown on the map — click to hide"}>
                <i className="zone-dot" style={{ background: p.color }} />
              </button>
              <button className="zt-name" onClick={() => map.flyTo({ center: [p.lng, p.lat], zoom: Math.max(map.getZoom(), 15) })} title="Show on map">
                <span><b>{p.name}</b><span className="sub">{multi ? `${orgName(p.org_id)} · ` : ""}{p.lat.toFixed(5)}, {p.lng.toFixed(5)}</span></span>
              </button>
              {canEdit(p) && <span className="zt-act">
                <button className="btn-ghost small" onClick={() => onEdit({ id: p.id, org_id: p.org_id, name: p.name, lat: p.lat, lng: p.lng, color: p.color, notes: p.notes ?? "" })}>Edit</button>
                <button className="btn-ghost small danger" onClick={() => remove(p)} aria-label={`Delete ${p.name}`}>✕</button>
              </span>}
            </li>
          ))}
          {!list.length && <li className="sub">{places.length ? "No places match." : "No saved places yet."}</li>}
        </ul>
      </div>
    </ToolPanel>
  );
}

/** Save / edit form (small panel). */
export function PlaceForm({ draft, orgs, onClose, onSaved }: { draft: PlaceDraft; orgs: PlaceOrg[]; onClose: () => void; onSaved: (p: Place) => void }) {
  const [f, setF] = useState<PlaceDraft>({ ...draft, org_id: draft.org_id ?? orgs[0]?.id });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function save() {
    if (!f.name.trim()) return setErr("Give the place a name.");
    if (!f.org_id) return setErr("You can't save places for this company.");
    setBusy(true); setErr(null);
    try {
      const body = { name: f.name.trim(), lat: f.lat, lng: f.lng, color: f.color, notes: f.notes.trim() || null, ...(f.id ? {} : { org_id: f.org_id }) };
      const p = await orgApi<Place>(f.id ? "PATCH" : "POST", f.id ? `places/${f.id}` : "places", body);
      rememberColour(f.color);
      onSaved(p);
    } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  }
  return (
    <ToolPanel title={f.id ? "Edit place" : "Save place"} onClose={onClose}
      footer={<><button className="btn-ghost small" onClick={onClose}>Cancel</button><button className="btn-primary small" disabled={busy} onClick={save}>{busy ? "Saving…" : "Save"}</button></>}>
      <div className="zt-form">
        <label>Name<input autoFocus value={f.name} maxLength={80} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Nadi depot" onKeyDown={(e) => e.key === "Enter" && save()} /></label>
        <p className="sub mono">{f.lat.toFixed(6)}, {f.lng.toFixed(6)} · {dms(f.lat, f.lng)}</p>
        {!f.id && orgs.length > 1 && (
          <label>Company<select value={f.org_id} onChange={(e) => setF({ ...f, org_id: e.target.value })}>
            {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}</select></label>
        )}
        <div><span className="pl-lbl">Colour</span><ColourPicker value={f.color} onChange={(c) => setF({ ...f, color: c ?? "#2563EB" })} /></div>
        <label>Notes<textarea rows={2} maxLength={500} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder="Optional — e.g. opening hours, who to call" /></label>
        {err && <div className="error">{err}</div>}
      </div>
    </ToolPanel>
  );
}
